import { z } from 'zod';
import { isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * Where a shop's images live — the folder tree behind the gallery.
 *
 * A folder is a named container, nothing more: it has a name the shop can change, an optional
 * parent so folders nest, and a `kind` that says whether the shop made it or the product did.
 *
 * The `kind` is the whole design. Four folders — common, client, product, reference — are seeded
 * for every shop, and a great deal hangs off them: the uploader files a customer's own fabric
 * photo under `client`, a catalogue shot under `product`, an inspiration screenshot under
 * `reference`. Identifying those by NAME would be a bug waiting for the first shop that renames
 * "Reference" to "Inspiration", or the first Tamil-language install where it was never called
 * that in the first place. So the kind is the identity, the name is a label, and nothing anywhere
 * — here, in the API, or on the screen — may match a folder by its name string.
 *
 * `custom` is the shop's own. Anything a shop creates is `custom`; the four system folders exist
 * only through seeding, which is why {@link createGalleryFolderSchema} has no `kind` at all.
 *
 * Contract only. The models, the seeding and the endpoints are task 16; the pages are task 17.
 */

/** Every kind a folder can be. The first four are seeded; `custom` is what a shop creates. */
export const GALLERY_FOLDER_KINDS = ['common', 'client', 'product', 'reference', 'custom'] as const;
export type GalleryFolderKind = (typeof GALLERY_FOLDER_KINDS)[number];
export const galleryFolderKindSchema = z.enum(GALLERY_FOLDER_KINDS);

/** The four every shop gets. `custom` is absent on purpose — it is the shop's own. */
export const SYSTEM_GALLERY_FOLDER_KINDS = ['common', 'client', 'product', 'reference'] as const;
export type SystemGalleryFolderKind = (typeof SYSTEM_GALLERY_FOLDER_KINDS)[number];

/**
 * True for the four seeded kinds. This is the ONLY test for system-ness — there is deliberately
 * no `isSystem` flag on the folder, because a stored boolean can drift away from the kind and
 * then two sources disagree about whether a folder may be deleted.
 */
export function isSystemGalleryFolderKind(kind: string): kind is SystemGalleryFolderKind {
  return (SYSTEM_GALLERY_FOLDER_KINDS as readonly string[]).includes(kind);
}

/** One seeded folder, as task 16 provisions it. */
export interface SystemGalleryFolderSpec {
  readonly kind: SystemGalleryFolderKind;
  /** The English default. The web resolves `gallery.folder.<kind>` through i18n. */
  readonly defaultName: string;
  readonly sortOrder: number;
}

/**
 * The seed list, in the order the four appear in the gallery.
 *
 * Lives here rather than in the seeding code so that the names and their order are decided once
 * and the web app can render a folder the API has not returned yet without inventing a label.
 */
export const SYSTEM_GALLERY_FOLDERS: readonly SystemGalleryFolderSpec[] = [
  { kind: 'common', defaultName: 'Common', sortOrder: 0 },
  { kind: 'client', defaultName: 'Client', sortOrder: 1 },
  { kind: 'product', defaultName: 'Product', sortOrder: 2 },
  { kind: 'reference', defaultName: 'Reference', sortOrder: 3 },
];

export const GALLERY_FOLDER_NAME_MAX_LENGTH = 80;

/**
 * How deep the tree may go — a root folder is depth 1, so three levels in total.
 *
 * A schema cannot see the tree, so it cannot enforce this: depth, cycles ("make this folder a
 * child of its own grandchild"), and the refusal to reparent or delete a system folder are all
 * checked in task 16's service, where the ancestors are known. The number lives here so the
 * server and the "New folder" button agree on when to stop offering a parent.
 */
export const GALLERY_FOLDER_MAX_DEPTH = 3;

/**
 * `entityType` for an image that belongs to the shop's gallery rather than to an order.
 *
 * Exported so the uploader (task 17) and the endpoints (task 16) cannot disagree on the string.
 * Matches the media module's `ENTITY_TYPE_PATTERN`.
 */
export const GALLERY_MEDIA_ENTITY_TYPE = 'gallery';

export const galleryFolderNameSchema = z
  .string()
  .trim()
  .min(1, 'Name the folder')
  .max(GALLERY_FOLDER_NAME_MAX_LENGTH);

const galleryFolderFieldsSchema = z.object({
  name: galleryFolderNameSchema,
  /** The folder this one sits inside. Absent or null means a root folder. */
  parentId: objectIdSchema.nullish(),
});

/**
 * Creating a folder. Note what is NOT here: `kind`.
 *
 * Anything a shop creates is `custom`. A shop cannot mint a second "product" folder, because the
 * four system kinds are seeded exactly once each and code elsewhere resolves them by kind — two
 * folders answering to `product` would make that lookup ambiguous. A `kind` sent by a client is
 * stripped rather than rejected, the way every other unknown key on a request body is.
 */
export const createGalleryFolderSchema = galleryFolderFieldsSchema;
export type CreateGalleryFolderInput = z.input<typeof createGalleryFolderSchema>;

/**
 * Renaming or moving a folder. `kind` is never updatable — see above.
 *
 * `{}` is refused rather than treated as a no-op, mirroring `updateMediaSchema`: an empty patch
 * is a client that has lost track of what the user changed, and answering 200 hides it.
 */
export const updateGalleryFolderSchema = galleryFolderFieldsSchema
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateGalleryFolderInput = z.input<typeof updateGalleryFolderSchema>;

export const galleryFolderSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  kind: galleryFolderKindSchema,
  parentId: objectIdSchema.nullable(),
  sortOrder: z.number().int(),
  /** Images filed directly in this folder, not counting descendants. Computed by task 16. */
  mediaCount: z.number().int().nonnegative(),
  createdAt: isoDateStringSchema,
});
export type GalleryFolder = z.infer<typeof galleryFolderSchema>;

/**
 * The whole tree, unpaginated.
 *
 * A shop has tens of folders and the page renders them as a tree, which cannot be assembled from
 * a page of rows without the parents that page happened to omit. Deliberately no
 * `paginationQuerySchema` here.
 */
export const galleryFolderListSchema = z.object({ items: z.array(galleryFolderSchema) });
export type GalleryFolderList = z.infer<typeof galleryFolderListSchema>;
